import type { Auth0VueClient, Auth0VueClientOptions } from '@auth0/auth0-vue';
import { createAuth0 } from '@auth0/auth0-vue';
import { defineNuxtPlugin, useRuntimeConfig, navigateTo } from 'nuxt/app';
import { watch } from 'vue';
import { getPostLoginRedirect, setLoggedIn } from '~/composables/usePostLoginRedirect';
import { resolveAuthRedirectUri, isNativeApp, isBundledNativeOrigin, NATIVE_BUNDLE_ID } from '~/functions/appOrigin';
import { APP_WELCOME_PATH } from '~/functions/resolveLoginEntryPath';
import { resolvePaymentReturnRoute } from '~/functions/nativePaymentReturn';
import useAuthStore from '~/stores/auth';

export default defineNuxtPlugin((nuxtApp) => {
   if (process.server) {
      return;
   }

   const config = useRuntimeConfig();

   // True only for the packaged app running its own bundled assets — not for the Phase 0
   // diagnostic, which carries the Capacitor bridge but runs at a real origin.
   const isPackagedApp = isNativeApp() && isBundledNativeOrigin();

   const auth0Params: Auth0VueClientOptions = {
      domain: config.public.auth0Domain as string,
      clientId: config.public.auth0ClientId as string,
      authorizationParams: {
         audience: config.public.auth0Audience as string,
         // Web: window.location.origin + '/lms' (unchanged — auth routes are under /lms/).
         // Native: the Auth0 custom-scheme callback. See functions/appOrigin.ts.
         redirect_uri: resolveAuthRedirectUri(config.public.auth0Domain as string),
      },

      // Native-only token strategy. All three are no-ops on the web, where the default
      // in-memory cache plus silent-auth iframe works and is the safer posture.
      ...(isPackagedApp
         ? {
              // Silent auth uses a hidden iframe against the Auth0 domain, which needs a
              // third-party cookie. There is no such cookie in a Custom Tab / WKWebView
              // session, so refresh tokens are the only way to renew without another
              // trip through the browser. Requires "Allow Offline Access" on the API,
              // enabled 2026-08-09 (docs/auth0-settings.md).
              useRefreshTokens: true,

              // Do not fall back to the iframe when a refresh token is missing or
              // rejected. On native the fallback cannot succeed, and letting it try turns
              // a clean "please log in again" into a hang.
              useRefreshTokensFallback: false,

              // Without this the SDK keeps tokens in memory only, so every cold start of
              // the app would land on the welcome screen again. localStorage persists for
              // the WebView origin and survives relaunch. Acceptable here because refresh
              // token rotation is enabled on the Native application.
              cacheLocation: 'localstorage',
           }
         : {}),
   };

   const auth0 = createAuth0(auth0Params);

   nuxtApp.vueApp.use(auth0);
   nuxtApp.provide('auth0', auth0);

   const unwatchLoading = watch(
      () => auth0.isLoading.value,
      async (isLoading) => {
         if (isLoading) {
            return;
         }

         unwatchLoading();

         // When Auth0 restores an existing session on a normal load (not a login
         // callback — that path sets the token itself), the accessToken was never
         // populated. Without it, authenticated views mounted on `isAuthenticated`
         // fired requests with no token. Fetch it here so the token exists as soon
         // as the app knows the user is authenticated. A failure (e.g. expired
         // refresh token) leaves accessToken undefined, keeping the user on public
         // views instead of sending an invalid token.
         if (!auth0.isAuthenticated.value) {
            return;
         }

         const authStore = useAuthStore();
         if (authStore.accessToken) {
            return;
         }

         try {
            authStore.accessToken = await auth0.getAccessTokenSilently();
         } catch (error) {
            console.error('Failed to restore Auth0 access token', error);
         }
      },
      { immediate: true }
   );

   /**
    * Everything that has to happen once a login has actually completed.
    *
    * Extracted because there are now two ways to arrive here and they must land the user
    * in the same place. On the web the authorization code comes back in the page URL; in
    * the packaged app it arrives through the custom scheme and `window.location` never
    * changes at all, so the web check below can never fire on native.
    */
   const completeLogin = async (client: Auth0VueClient) => {
      // Fetch token and profile before deciding redirect target.
      // The API returns post_signup_redirect in site_settings when a
      // first-time user should be sent to a specific page (e.g. memberships).
      // The profile await also ensures Auth0's internal navigation settles
      // before we call navigateTo.
      setLoggedIn();

      // Signal the middleware that a callback redirect is pending.
      // During the profile await, Auth0 SDK navigates to / internally,
      // which would flash the public homepage. The middleware checks this
      // flag and redirects immediately instead.
      sessionStorage.setItem('auth0_callback_pending', 'true');
      let targetUrl = getPostLoginRedirect();

      try {
         const authStore = useAuthStore();

         if (!authStore.accessToken) {
            authStore.accessToken = await client.getAccessTokenSilently();
         }

         await authStore.setCurrentUserProfile();

         // Persist portal selection on first login (covers /login/[portal] and ?portal= flows)
         const selectedPortal = localStorage.getItem('selectedPortal');
         if (selectedPortal) {
            authStore.persistPortalSelection(selectedPortal);
         }

         const postSignupRedirect = authStore.user.site_settings.post_signup_redirect;
         if (postSignupRedirect) {
            targetUrl = postSignupRedirect;
         }
      } catch (error) {
         // Login itself succeeded — only the post-login setup above failed — so we still
         // redirect, using the fallback target. But the app lands in a degraded state that
         // looks like unrelated UI bugs rather than an auth failure: an empty profile means
         // `site_settings.nav_items` is empty, so the nav drawer renders nothing but the
         // hardcoded Logout link, and authenticated views show `undefined` where API data
         // should be. Swallowing this silently made that impossible to diagnose from the
         // console. See docs/mobile-app-status.md.
         console.error('[auth0] Post-login setup failed; continuing with degraded profile:', error);
      }

      // Use setTimeout to ensure Auth0 has fully completed its internal state updates
      setTimeout(() => {
         navigateTo(targetUrl, { replace: true });
      }, 0);
   };

   // Native callback. The system browser hands the custom-scheme URL back to the app as
   // an `appUrlOpen` event; the SDK cannot see it, so we pass it to handleRedirectCallback
   // explicitly. Registered before any login is attempted, since the event can arrive as
   // soon as the browser closes.
   if (isPackagedApp) {
      void (async () => {
         const [{ App }, { Browser }] = await Promise.all([
            import('@capacitor/app'),
            import('@capacitor/browser'),
         ]);

         await App.addListener('appUrlOpen', async ({ url }) => {
            // Universal Links would arrive over https and are left to fall through
            // untouched. Everything on the custom scheme is ours to route.
            if (!url.startsWith(`${NATIVE_BUNDLE_ID}://`)) return;

            // The Stripe return trip, and it has to be answered **before** the logout
            // branch below: a payment deep link carries no `state=`/`code=`, so that
            // branch would read it as a logout and sign the user out mid-purchase. The
            // browser is closed here rather than left for the user to dismiss, which is
            // the whole point of having opened it ourselves.
            const paymentReturnRoute = resolvePaymentReturnRoute(url);

            if (paymentReturnRoute) {
               await Browser.close().catch(() => undefined);

               return navigateTo(paymentReturnRoute, { replace: true });
            }

            const isLoginCallback = url.includes('state=') && (url.includes('code=') || url.includes('error='));

            // Logout comes back to the *same* callback URL but with no query string at
            // all, so the login-shaped guard above used to drop it — which is why the
            // button looked dead and the Custom Tab was never closed.
            //
            // By this point the SDK has already cleared its own cache, yet nothing has
            // navigated. On the web `logout()` sends the browser to Auth0 and back to the
            // origin, so the app reboots and every store starts empty; here the WebView
            // never moved, so the Pinia store still holds the user and the nav keeps
            // rendering them as signed in. Resetting it and returning to the welcome
            // screen is what that full page load does on the web.
            if (!isLoginCallback) {
               await Browser.close().catch(() => undefined);
               useAuthStore().$reset();

               return navigateTo(APP_WELCOME_PATH, { replace: true });
            }

            try {
               await auth0.handleRedirectCallback(url);
               await completeLogin(auth0);
            } catch (error) {
               console.error('Auth0 native callback failed', error);
            } finally {
               // iOS keeps SFSafariViewController on top until dismissed. On Android the
               // Custom Tab is already behind the re-focused activity and this is a no-op.
               await Browser.close().catch(() => undefined);
            }
         });
      })();
   }

   // Web callback. Watches for Auth0 to finish processing the code in the page URL, which
   // prevents NAVIGATION_DUPLICATED errors from navigating before the SDK settles.
   const hasCodeInUrl = window.location.search.includes('code=');
   if (hasCodeInUrl) {
      let callbackExecuted = false;

      const unwatch = watch(
         () => auth0.isLoading.value,
         async (isLoading) => {
            if (callbackExecuted) return;

            // When Auth0 finishes loading (callback processed), navigate to target
            if (!isLoading && auth0.isAuthenticated.value) {
               callbackExecuted = true;

               // unwatch() is called after the await to avoid temporal dead zone
               // when immediate: true fires the callback before watch() returns
               await completeLogin(auth0);

               unwatch();
            }
         },
         { immediate: true }
      );
   }
});
